import React, { useState } from 'react';
import Header from 'core/layout/Header';
import MinimalFooter from '../components/MinimalFooter';
import SearchHero from 'core/components/SearchHero';
import { Search, MapPin, UserPlus, FileText, Rocket, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import HomeJobsSidebar from '../components/HomeJobsSidebar';
import HomeJobDetailsPanel from '../components/HomeJobDetailsPanel';
import { useMutation, useQueryClient } from 'react-query';
import { HttpService } from 'core/services/http';
import { Box, Button, Container, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'core/context/TranslationContext';

function HomePage() {
    const [selectedJob, setSelectedJob] = useState(null);
    const [searchFilters, setSearchFilters] = useState({});
    const [isSearching, setIsSearching] = useState(false);
    const queryClient = useQueryClient();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { _t } = useTranslation();

    // Bookmark mutation
    const bookmarkMutation = useMutation(
        async (jobId) => {
            const service = new HttpService();
            return await service.post('/job/bookmark/', { job: jobId });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['homeJobs']);
                if (selectedJob) {
                    setSelectedJob(prev => ({
                        ...prev,
                        is_bookmarked: !prev.is_bookmarked
                    }));
                }
            }
        }
    );

    // Hide job mutation
    const hideJobMutation = useMutation(
        async (jobId) => {
            const service = new HttpService();
            return await service.post('/job/hide/', { job: jobId });
        },
        {
            onSuccess: (_, jobId) => {
                if (selectedJob?.id === jobId) {
                    setSelectedJob(null);
                }
                queryClient.invalidateQueries(['homeJobs']);
            }
        }
    );

    const handleSearch = (filters) => {
        setSearchFilters(filters);
        setSelectedJob(null); // Clear selection on new search
    };

    const handleJobSelect = (job) => {
        setSelectedJob(job);
    };

    const handleBookmark = (jobId, isBookmarked) => {
        bookmarkMutation.mutate(jobId);
    };

    const handleHide = (jobId) => {
        hideJobMutation.mutate(jobId);
    };

    const [jobTitle, setJobTitle] = useState('');
    const [place, setPlace] = useState('');

    const runSearch = (title, loc) => {
        const params = {};
        if (title.trim()) params.search = title.trim();
        if (loc.trim()) params.location = loc.trim();
        handleSearch(params);
        document.getElementById('latest-jobs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const submitSearch = (e) => {
        e.preventDefault();
        runSearch(jobTitle, place);
    };

    const quickSearch = (term) => {
        setJobTitle(term);
        runSearch(term, place);
    };

    const POPULAR = ['Developer', 'Designer', 'Engineer', 'Marketing', 'Sales', 'Nurse'];

    const STEPS = [
        { icon: UserPlus, title: 'Create your profile', text: 'Sign up in minutes and tell employers who you are, what you do best and where you want to go.' },
        { icon: FileText, title: 'Build a standout CV', text: 'Use our CV builder to generate a polished, shareable CV that is ready for every application.' },
        { icon: Rocket, title: 'Apply and get hired', text: 'Find roles that fit, apply in one click and track every application in one place.' }
    ];

    return (
        <>
            <Header />

            <SearchHero
                eyebrow={_t('Thousands of opportunities, one place')}
                title={<>{_t('Find the job that')} <em>{_t('moves you forward')}</em></>}
                lead={_t('Discover roles from top employers, build a great CV and connect with opportunities worldwide.')}
                onSubmit={submitSearch}
                loading={isSearching}
                submitLabel={_t('Find jobs')}
                fields={[
                    { key: 'q', placeholder: _t('Job title, keywords or company'), icon: Search, value: jobTitle, onChange: setJobTitle },
                    { key: 'loc', placeholder: _t('City or postcode'), icon: MapPin, value: place, onChange: setPlace }
                ]}
            >
                <div className="jf-tags">
                    <span>{_t('Popular')}:</span>
                    {POPULAR.map((term) => (
                        <button key={term} type="button" className="jf-tag" onClick={() => quickSearch(term)}>
                            {_t(term)}
                        </button>
                    ))}
                </div>
            </SearchHero>

            <Box id="latest-jobs" className="jf-public" sx={{ scrollMarginTop: '84px', py: { xs: 4, md: 6 } }}>
                <Container maxWidth="xl">
                    <div className="jf-results-bar">
                        <h2>{searchFilters.search || searchFilters.location ? _t('Search results') : _t('Latest opportunities')}</h2>
                        {(searchFilters.search || searchFilters.location) && (
                            <Button size="small" onClick={() => { setJobTitle(''); setPlace(''); handleSearch({}); }}>
                                {_t('Clear search')}
                            </Button>
                        )}
                    </div>
                    <Grid container spacing={2.5}>
                        <Grid
                            item
                            xs={12}
                            md={selectedJob && !isMobile ? 5 : 12}
                            sx={{ display: { xs: selectedJob ? 'none' : 'block', md: 'block' } }}
                        >
                            <HomeJobsSidebar
                                searchFilters={searchFilters}
                                onJobSelect={handleJobSelect}
                                selectedJobId={selectedJob?.id}
                                onLoadingChange={setIsSearching}
                            />
                        </Grid>

                        {selectedJob && (
                            <Grid item xs={12} md={7}>
                                <Box sx={{ position: 'sticky', top: 88, maxHeight: 'calc(100vh - 108px)', overflowY: 'auto', borderRadius: 4 }}>
                                    <HomeJobDetailsPanel
                                        job={selectedJob}
                                        onBookmark={handleBookmark}
                                        onHide={handleHide}
                                        isBookmarked={selectedJob.is_bookmarked}
                                    />
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Container>
            </Box>

            <section className="jf-public jf-section jf-section--white">
                <div className="jf-container">
                    <div className="jf-section__head">
                        <span className="jf-kicker">{_t('How it works')}</span>
                        <h2>{_t('Your next role in three simple steps')}</h2>
                        <p>{_t('From profile to offer, we keep the journey clear and fast.')}</p>
                    </div>
                    <div className="jf-steps">
                        {STEPS.map(({ icon: Icon, title, text }, i) => (
                            <div className="jf-step" key={title}>
                                <span className="jf-step__num">0{i + 1}</span>
                                <div className="jf-step__icon"><Icon size={24} /></div>
                                <h3>{_t(title)}</h3>
                                <p>{_t(text)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="jf-public jf-section">
                <div className="jf-container">
                    <div className="jf-cta">
                        <h2>{_t('Hiring? Reach the right people faster.')}</h2>
                        <p>{_t('Post your jobs, browse candidate profiles and manage every application from one dashboard.')}</p>
                        <div className="jf-cta__actions">
                            <Link to="/signup" className="jf-btn jf-btn--white">{_t('Post a job')} <ArrowRight size={18} /></Link>
                            <Link to="/candidates" className="jf-btn jf-btn--ghost">{_t('Browse candidates')}</Link>
                        </div>
                    </div>
                </div>
            </section>

            <MinimalFooter />
        </>
    );
}

export default HomePage;
